"use client";

import React, { createContext, useContext, useMemo } from "react";
import { t, type UiStringKey } from "../i18n/strings";
import { isExternalHref, resolveLinkHref, type LinkValue } from "../utils/links";

/** Runtime image component props, compatible with next/image and native img. */
export type RuntimeImageProps = Omit<React.ImgHTMLAttributes<HTMLImageElement>, "src"> & {
  src: string;
  /**
   * Optional Next.js style props are accepted for compatibility but ignored
   * when falling back to the native <img /> element.
   */
  fill?: boolean;
  priority?: boolean;
  showLoadingCircle?: boolean;
};

/** Runtime link component props, compatible with next/link and native anchor. */
export type RuntimeLinkProps = Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, "href"> & {
  href?: LinkValue;
};

/** Runtime link props with per-link disable option. */
export type RuntimeLinkComponentProps = RuntimeLinkProps & {
  /**
   * Per-link opt-out that prevents navigation when true.
   */
  disableClicks?: boolean;
};

/**
 * Resolves a non-image storage asset (e.g. a PDF) referenced by a
 * bucket-relative key into a browser-loadable absolute URL.
 *
 * Images flow through `imageComponent`, which resolves the `src` internally.
 * Other assets — PDF previews/downloads on data-record pages — have no
 * component wrapper, so the consuming app injects this resolver and the
 * renderers call it before emitting an `<iframe src>` / `<a href>`.
 *
 * The resolver must pass already-absolute URLs (`http(s)://`, `data:`,
 * `blob:`) through unchanged and prefix bucket-relative keys with the
 * environment-appropriate resource base URL.
 */
export type AssetUrlResolver = (key: string) => string;

/** Runtime context value with customizable image/link components. */
export interface RendererRuntimeValue {
  /**
   * Optionally provide a custom image component (e.g., next/image).
   */
  imageComponent?: React.ComponentType<RuntimeImageProps>;
  /**
   * Optionally provide a custom link component (e.g., next/link).
   */
  linkComponent?: React.ComponentType<RuntimeLinkProps>;
  /**
   * Optionally provide a resolver for non-image storage assets (e.g. PDFs).
   * Renderers call this to turn a bucket-relative key into an absolute URL.
   * When omitted, callers fall back to the raw key.
   */
  assetResolver?: AssetUrlResolver;
  /**
   * When true, all links are prevented from navigating (useful in preview panes).
   */
  disableLinks?: boolean;
  /**
   * Callback fired on every link click attempt (even when navigation is blocked).
   */
  onLinkClick?: (payload: { href?: string; event: React.MouseEvent<HTMLElement> }) => void;
  /**
   * Optional client-side navigation function (e.g. Next.js `router.push`).
   * Renderers use this for PROGRAMMATIC navigation (search form submit,
   * suggestion click) so it stays a SPA transition instead of a full-page
   * `window.location` reload. When not injected (admin previews, unit tests,
   * plain SDK consumers) callers fall back to `window.location`.
   */
  navigate?: (href: string) => void;
  /**
   * Current UI locale ("fr", "en", …). Components read it via `useLocale()`
   * to localize their STATIC text through `t()` — the CMS content itself is
   * already per-language in the payload. Defaults to French when not injected
   * (admin previews, tests, plain SDK consumers).
   */
  locale?: string;
  /**
   * Libellés d'interface édités dans le CMS (record `libelles-interface`),
   * déjà résolus dans la locale courante côté serveur. `useT()` les lit en
   * PRIORITÉ, puis retombe sur le dictionnaire code : sans injection (admin,
   * tests, consommateurs SDK bruts) le rendu est identique à `t()`.
   */
  uiStrings?: Record<string, string>;
}

const RendererRuntimeContext = createContext<RendererRuntimeValue>({
  disableLinks: false,
});

/** Hook to access the renderer runtime context (image, link, config). */
export function useRendererRuntime(): RendererRuntimeValue {
  return useContext(RendererRuntimeContext);
}

/**
 * Returns a client-side navigation function. Uses the injected runtime
 * `navigate` (e.g. Next.js `router.push`) for SPA transitions; falls back to
 * a full-page `window.location.href` assignment when none is injected. Keeps
 * the SDK framework-agnostic while letting the Next.js app avoid full reloads
 * (which would otherwise re-trigger one-shot UI like the experimental notice).
 */
export function useRuntimeNavigate(): (href: string) => void {
  const { navigate } = useRendererRuntime();
  return useMemo(
    () => (href: string) => {
      if (navigate) {
        navigate(href);
      } else if (typeof window !== "undefined") {
        window.location.href = href;
      }
    },
    [navigate],
  );
}

/**
 * Current UI locale from the runtime. French — the site's source language —
 * when none is injected, so every existing consumer renders unchanged.
 */
export function useLocale(): string {
  const { locale } = useRendererRuntime();
  return locale && locale.trim() ? locale.trim().toLowerCase() : "fr";
}

/**
 * Traduction d'un libellé d'interface : record CMS `libelles-interface` (injecté
 * dans le runtime, déjà résolu par locale avec fallback français côté serveur),
 * puis dictionnaire code `t()` en dernier recours. C'est la version ADMIN-ÉDITABLE
 * de `t()` — les renderers qui affichent du chrome doivent passer par elle.
 */
export function useT(): (key: UiStringKey) => string {
  const { locale, uiStrings } = useRendererRuntime();
  const resolvedLocale = locale && locale.trim() ? locale.trim().toLowerCase() : "fr";
  return useMemo(
    () => (key: UiStringKey) => {
      const fromCms = uiStrings?.[key];
      return typeof fromCms === "string" && fromCms.length > 0 ? fromCms : t(resolvedLocale, key);
    },
    [resolvedLocale, uiStrings],
  );
}

/**
 * Resolves a non-image storage asset key (e.g. a PDF) through the runtime
 * `assetResolver`. Returns the trimmed key unchanged when no resolver is
 * injected, so component behaviour is unaffected outside the Next.js app
 * (admin previews, tests).
 */
export function useAssetUrl(key?: string | null): string {
  const { assetResolver } = useRendererRuntime();
  const trimmed = (key ?? "").trim();
  if (!trimmed) return "";
  return assetResolver ? assetResolver(trimmed) : trimmed;
}

/** Runtime context provider props. */
export interface RendererRuntimeProviderProps {
  value?: RendererRuntimeValue;
  children: React.ReactNode;
}

/** Runtime context provider for customizable image and link components. */
export function RendererRuntimeProvider({ value, children }: RendererRuntimeProviderProps) {
  const merged = useMemo<RendererRuntimeValue>(
    () => ({
      disableLinks: false,
      ...value,
    }),
    [value],
  );

  return <RendererRuntimeContext.Provider value={merged}>{children}</RendererRuntimeContext.Provider>;
}

/** Runtime image component that delegates to a custom component or native img tag.
 *
 * Resolution strategy:
 *   - When `imageComponent` is injected (Next.js app via next/image), pass
 *     `src` through unchanged — the consumer's own loader handles URL
 *     building (relative bucket key → absolute CDN URL).
 *   - When no `imageComponent` is configured (admin previews, isolated unit
 *     tests, plain SDK consumers), resolve `src` through the runtime
 *     `assetResolver` so a bucket-relative key still becomes a fetchable URL.
 *     Falls back to the raw `src` when no resolver is configured either —
 *     same shape as the bare `<img src={key} />` we used to emit.
 */
export function RuntimeImage(props: RuntimeImageProps) {
  const { imageComponent: ImageComponent } = useRendererRuntime();
  const { onLoad, onError, ...restProps } = props;
  // Resolved bucket-key → URL for the native-img fallback. Hook call is
  // unconditional so React's hook-order invariant holds across renders;
  // when ImageComponent is present the resolved value is simply unused.
  const resolvedSrc = useAssetUrl(typeof props.src === "string" ? props.src : undefined);

  const renderImage = () => {
    if (ImageComponent) {
      const { width, height, fill, sizes, ...rest } = restProps;
      const needsDimensions = !fill && (width === undefined || height === undefined);
      const finalProps: RuntimeImageProps = needsDimensions
        ? {
            ...rest,
            src: props.src,
            alt: props.alt,
            width: width ?? 1600,
            height: height ?? 900,
            sizes: sizes ?? "100vw",
            priority: props.priority,
            onLoad,
            onError,
          }
        : { ...restProps, onLoad, onError };

      return <ImageComponent {...finalProps} />;
    }

    const { fill: _fill, priority: _priority, src: _origSrc, ...rest } = restProps;
    const effectiveSrc = resolvedSrc || props.src;
    return <img {...rest} src={effectiveSrc} onLoad={onLoad} onError={onError} />;
  };

  return renderImage();
}

/** Runtime link component that delegates to a custom component or native anchor tag. */
export function RuntimeLink({ href, onClick, disableClicks, target, rel, ...rest }: RuntimeLinkComponentProps) {
  const { linkComponent: LinkComponent, disableLinks, onLinkClick } = useRendererRuntime();

  const resolvedHref = resolveLinkHref(href);

  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    const shouldBlock = disableLinks || disableClicks;
    if (shouldBlock) {
      event.preventDefault();
      event.stopPropagation();
    }

    onLinkClick?.({ href: resolvedHref, event });
    onClick?.(event);
  };

  const finalHref = resolvedHref ?? "#";

  // External URLs open in a new tab by default (callers can override with their
  // own target/rel). The rel string also gets noopener/noreferrer to neutralise
  // window.opener leaks and referer headers — basic safe-default for outbound.
  const external = isExternalHref(href);
  const finalTarget = target ?? (external ? "_blank" : undefined);
  const finalRel = rel ?? (external ? "noopener noreferrer" : undefined);

  if (LinkComponent) {
    return <LinkComponent {...rest} href={finalHref} onClick={handleClick} target={finalTarget} rel={finalRel} />;
  }

  return <a {...rest} href={finalHref} onClick={handleClick} target={finalTarget} rel={finalRel} />;
}

